import { useState, FormEvent } from 'react';
import { Download, Sparkles, Send, Settings, Check } from 'lucide-react';
import { usePrototype1 } from '../Prototype1Context';
import DraftBody from '../../_shared/DraftBody';

const SECTION_ID = 'college-eligibility';

const Screen03EquityGapsDrafting = () => {
  const { chatHistoriesBySection, isChatPending, sendChat, goToScreen, drafts, approveSection, canUndoDraft } =
    usePrototype1();
  const [input, setInput] = useState('');
  const chatHistory = chatHistoriesBySection[SECTION_ID] ?? [];
  const draft = drafts[SECTION_ID] ?? '';
  const showApprove = canUndoDraft(SECTION_ID);

  const handleApprove = () => {
    approveSection(SECTION_ID);
    goToScreen('05-dashboard-report-overview');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isChatPending) return;
    const message = input;
    setInput('');
    await sendChat(message, {
      intent: 'edit-current-draft',
      active_section_id: SECTION_ID,
      current_draft: draft,
      ui_state: { panel: 'chat-open' },
      response_hint: { shape: 'rewrite-paragraph' },
    });
  };

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between px-6 h-14 border-b border-[#e5e5e5] shrink-0">
          <div className="flex items-center gap-2 text-sm">
            <button type="button" onClick={() => goToScreen('01-dashboard')} className="text-[#737373] hover:underline">
              Issaquah School District
            </button>
            <span className="text-[#737373]">/</span>
            <span className="text-[#737373]">Class of 2025</span>
            <span className="text-[#737373]">/</span>
            <span className="font-medium text-[#0a0a0a]">College Eligibility</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-white rounded-full bg-[#f59e0b]">
              Drafting
            </span>
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium border border-[#e5e5e5] rounded-md bg-white hover:bg-gray-50 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export Draft
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="flex flex-col flex-1 min-w-0 bg-[#fafafa]">
            <div className="flex items-center justify-between px-6 h-14 border-b border-[#e5e5e5] shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-[#0a0a0a]">College Eligibility</span>
                <span className="text-xs text-[#737373]">Last edited just now</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium text-[#f59e0b] bg-[#fef3c7] rounded">
                  AI Drafting
                </span>
                {showApprove && (
                  <button
                    type="button"
                    onClick={handleApprove}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-[#171717] hover:bg-[#2a2a2a] rounded-md transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Approve Section
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-5 p-8 overflow-y-auto flex-1">
              <h1 className="text-[22px] font-bold text-[#0a0a0a]">College Eligibility</h1>
              <h2 className="text-[17px] font-semibold text-[#0a0a0a]">Equity Gaps in College Eligibility</h2>
              <DraftBody text={draft} />
            </div>
          </div>

          <div className="flex flex-col w-[420px] shrink-0 border-l border-[#e5e5e5] bg-[#fafafa]">
            <div className="flex items-center justify-between px-4 h-12 border-b border-[#e5e5e5] shrink-0">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#171717]">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-[13px] font-semibold text-[#0a0a0a]">AI Assistant</span>
              </div>
              <button
                type="button"
                onClick={() => goToScreen('04-chat-panel-closed')}
                className="text-[#737373] hover:text-[#0a0a0a]"
                aria-label="Close chat panel"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-col gap-4 flex-1 overflow-y-auto p-4">
              {chatHistory.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'items-start gap-2'}`}>
                  {m.role === 'assistant' && (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#171717] shrink-0 mt-0.5">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div
                    className={`rounded-lg px-3 py-2.5 ${
                      m.role === 'user'
                        ? 'bg-[#171717] text-white max-w-[80%]'
                        : 'bg-[#f5f5f5] border border-[#e5e5e5] text-[#0a0a0a] flex-1'
                    }`}
                  >
                    {m.role === 'user' ? (
                      <p className="text-[13px] leading-[1.5]">{m.content}</p>
                    ) : (
                      <DraftBody text={m.content} />
                    )}
                  </div>
                </div>
              ))}
              {isChatPending && <span className="text-xs text-[#737373]">AI is typing…</span>}
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 px-4 py-3 border-t border-[#e5e5e5] shrink-0"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask or instruct the AI..."
                disabled={isChatPending}
                className="flex-1 h-10 px-3 rounded-lg bg-[#fafafa] border border-[#e5e5e5] text-[13px] text-[#0a0a0a] outline-none"
              />
              <button
                type="submit"
                disabled={isChatPending || !input.trim()}
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#171717] shrink-0 hover:bg-[#2a2a2a] transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screen03EquityGapsDrafting;
